import { z } from "zod";

/** Indian states / UTs with GST state codes (first two digits of a GSTIN). */
export const INDIAN_STATES = [
  { code: "01", name: "Jammu and Kashmir" },
  { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" },
  { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" },
  { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "10", name: "Bihar" },
  { code: "11", name: "Sikkim" },
  { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" },
  { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" },
  { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" },
  { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" },
  { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" },
  { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "24", name: "Gujarat" },
  { code: "26", name: "Dadra and Nagar Haveli and Daman and Diu" },
  { code: "27", name: "Maharashtra" },
  { code: "29", name: "Karnataka" },
  { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" },
  { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" },
  { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman and Nicobar Islands" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "38", name: "Ladakh" },
  { code: "97", name: "Other Territory" },
] as const;

export type GstStateCode = (typeof INDIAN_STATES)[number]["code"];
export const gstStateCodeSchema = z.enum(INDIAN_STATES.map((s) => s.code) as [GstStateCode, ...GstStateCode[]]);

export function stateName(code: string): string | undefined {
  return INDIAN_STATES.find((s) => s.code === code)?.name;
}

const GSTIN_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/** Validates GSTIN structure and its mod-36 check digit. */
export function isValidGstin(input: string): boolean {
  const gstin = input.trim().toUpperCase();
  if (!GSTIN_REGEX.test(gstin)) return false;
  if (!INDIAN_STATES.some((s) => s.code === gstin.slice(0, 2))) return false;
  return gstinCheckDigit(gstin.slice(0, 14)) === gstin[14];
}

export function gstinCheckDigit(first14: string): string {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const v = GSTIN_CHARS.indexOf(first14[i]!);
    const product = v * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return GSTIN_CHARS[(36 - (sum % 36)) % 36]!;
}

export const gstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine(isValidGstin, { message: "Invalid GSTIN" });

export const panSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Invalid PAN");

/** Common SAC codes for agency services (GST rate 18% at time of writing). */
export const AGENCY_SAC_CODES = [
  { code: "998361", description: "Advertising services provided by advertising agencies" },
  { code: "998362", description: "Purchase or sale of advertising space or time, on commission" },
  { code: "998363", description: "Sale of advertising space in print media (except on commission)" },
  { code: "998365", description: "Sale of internet advertising space (except on commission)" },
  { code: "998366", description: "Sale of other advertising space or time (except on commission)" },
  { code: "998371", description: "Market research services" },
  { code: "998372", description: "Public opinion polling services" },
  { code: "998311", description: "Management consulting and management services" },
  { code: "998312", description: "Business consulting services including public relations services" },
  { code: "998313", description: "Information technology (IT) consulting and support services" },
  { code: "998314", description: "Information technology (IT) design and development services" },
  { code: "998315", description: "Hosting and IT infrastructure provisioning services" },
  { code: "998391", description: "Specialty design services (interior, industrial, fashion, graphic)" },
  { code: "998392", description: "Design originals" },
  { code: "998393", description: "Scientific and technical consulting services" },
  { code: "998397", description: "Sponsorship services and brand promotion services" },
  { code: "998399", description: "Other professional, technical and business services n.e.c." },
  { code: "998386", description: "Photography & videography and their processing services" },
  { code: "999612", description: "Motion picture, videotape and television programme production" },
] as const;

export const GST_RATES_BPS = [0, 500, 1200, 1800, 2800] as const;
export const DEFAULT_GST_RATE_BPS = 1800;

export type GstSupplyType = "intra_state" | "inter_state" | "export";

/**
 * Place-of-supply rule for B2B services: supplier state == recipient state →
 * CGST+SGST (split equally); different state → IGST; recipient outside India →
 * export (zero-rated under LUT, or IGST paid).
 */
export function gstSupplyType(supplierStateCode: string | null | undefined, recipientStateCode: string | null | undefined, recipientCountry: string): GstSupplyType {
  if (recipientCountry && recipientCountry.toUpperCase() !== "IN") return "export";
  if (supplierStateCode && recipientStateCode && supplierStateCode === recipientStateCode) return "intra_state";
  return "inter_state";
}

export interface GstBreakup {
  cgstMinor: number;
  sgstMinor: number;
  igstMinor: number;
  totalTaxMinor: number;
}

/** Tax on a taxable amount. CGST/SGST split so the halves always sum to the total. */
export function computeGst(taxableMinor: number, rateBps: number, supply: GstSupplyType, exportUnderLut = true): GstBreakup {
  if (supply === "export" && exportUnderLut) return { cgstMinor: 0, sgstMinor: 0, igstMinor: 0, totalTaxMinor: 0 };
  const total = Math.round((taxableMinor * rateBps) / 10_000);
  if (supply === "intra_state") {
    const cgst = Math.floor(total / 2);
    return { cgstMinor: cgst, sgstMinor: total - cgst, igstMinor: 0, totalTaxMinor: total };
  }
  return { cgstMinor: 0, sgstMinor: 0, igstMinor: total, totalTaxMinor: total };
}

/** Indian financial year (April–March) label for a date, e.g. "2026-27". */
export function financialYear(date: Date | string, fyStartMonth = 4): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const start = m >= fyStartMonth ? y : y - 1;
  if (fyStartMonth === 1) return String(start);
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

/** GST invoice numbers: max 16 chars, only A–Z, 0–9, "-" and "/", unique per FY. */
export const GST_INVOICE_NUMBER_REGEX = /^[A-Za-z0-9\-/]{1,16}$/;

/** Formats a number in Indian words (used on invoices: "Rupees One Lakh Twenty Thousand Only"). */
export function amountInWordsINR(minor: number): string {
  const rupees = Math.floor(Math.abs(minor) / 100);
  const paise = Math.abs(minor) % 100;
  const words = (n: number): string => {
    const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    if (n < 20) return a[n]!;
    if (n < 100) return `${b[Math.floor(n / 10)]}${n % 10 ? " " + a[n % 10] : ""}`;
    if (n < 1000) return `${a[Math.floor(n / 100)]} Hundred${n % 100 ? " " + words(n % 100) : ""}`;
    if (n < 100000) return `${words(Math.floor(n / 1000))} Thousand${n % 1000 ? " " + words(n % 1000) : ""}`;
    if (n < 10000000) return `${words(Math.floor(n / 100000))} Lakh${n % 100000 ? " " + words(n % 100000) : ""}`;
    return `${words(Math.floor(n / 10000000))} Crore${n % 10000000 ? " " + words(n % 10000000) : ""}`;
  };
  const r = rupees === 0 ? "Zero" : words(rupees);
  return `Rupees ${r}${paise ? ` and ${words(paise)} Paise` : ""} Only`;
}
